<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-2">
              <div class="col-sm-6">
                <div class="search-box mr-2 mb-2 d-inline-block">
                  <div class="position-relative">
                    <input
                      type="text"
                      class="form-control"
                      v-model="search"
                      placeholder="Buscar por nome..."
                      @keyup.enter="loadCards"
                    />
                    <i class="bx bx-search-alt search-icon"></i>
                  </div>
                </div>
              </div>
              <div class="col-sm-6">
                <div class="float-sm-end d-flex justify-content-end">
                  <button type="button" class="btn btn-success btn-rounded mb-2" @click="openNewCardModal">
                    <i class="mdi mdi-plus mr-1"></i> Nova Ficha
                  </button>
                </div>
              </div>
            </div>

            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Carregando...</span>
              </div>
            </div>

            <div v-else-if="cards.length === 0" class="text-center py-5">
              <i class="bx bx-id-card" style="font-size: 64px; color: #ced4da;"></i>
              <h5 class="mt-3 text-muted">Nenhuma ficha cadastrada</h5>
              <p class="text-muted">Crie sua primeira ficha do participante para começar.</p>
              <button class="btn btn-success btn-rounded mt-2" @click="openNewCardModal">
                <i class="mdi mdi-plus mr-1"></i> Criar Primeira Ficha
              </button>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-centered table-nowrap table-hover">
                <thead>
                  <tr>
                    <th width="30%">Nome</th>
                    <th width="25%">Grupo</th>
                    <th width="15%">Status</th>
                    <th width="10%">Padrão</th>
                    <th width="20%">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="card in cards" :key="card.id">
                    <td class="link" @click="editCard(card.id)">
                      <span class="badge font-size-14 badge-soft-primary" style="min-width:100px">{{ card.name }}</span>
                      <div v-if="card.description" class="text-muted small mt-1">{{ card.description }}</div>
                    </td>
                    <td>
                      <span v-if="card.group_id && card.group" class="badge badge-soft-info">
                        {{ card.group.name || card.group.data && card.group.data.name }}
                      </span>
                      <span v-else class="badge badge-soft-secondary">Global</span>
                    </td>
                    <td>
                      <b-dropdown
                        size="sm"
                        style="min-width:100px"
                        :variant="card.is_active ? 'success' : 'danger'"
                      >
                        <template v-slot:button-content>
                          {{ card.is_active ? 'Ativa' : 'Inativa' }}
                          <i class="mdi mdi-chevron-down"></i>
                        </template>
                        <b-dropdown-item-button @click="toggleStatus(card)">
                          {{ card.is_active ? 'Inativar' : 'Ativar' }}
                        </b-dropdown-item-button>
                      </b-dropdown>
                    </td>
                    <td>
                      <span v-if="card.is_default" class="badge badge-soft-warning">
                        <i class="bx bx-star"></i> Padrão
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td>
                      <button
                        type="button"
                        class="btn btn-outline-primary btn-sm mr-1"
                        @click="editCard(card.id)"
                        title="Editar"
                      >
                        <i class="bx bx-edit"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-outline-info btn-sm mr-1"
                        @click="duplicateCard(card)"
                        title="Duplicar"
                      >
                        <i class="bx bx-copy"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-outline-danger btn-sm"
                        @click="confirmDeleteCard(card)"
                        title="Deletar"
                      >
                        <i class="bx bx-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Nova Ficha -->
    <b-modal
      v-model="showNewCardModal"
      title="Nova Ficha"
      ok-title="Criar"
      cancel-title="Cancelar"
      :ok-disabled="!newCard.name"
      @ok.prevent="createCard"
      centered
    >
      <div class="form-group">
        <label>Nome *</label>
        <input
          v-model="newCard.name"
          type="text"
          class="form-control"
          placeholder="Ex: Ficha — Sem Acompanhante"
          autofocus
        />
      </div>
      <div class="form-group">
        <label>Descrição</label>
        <textarea
          v-model="newCard.description"
          class="form-control"
          rows="2"
          placeholder="Descrição opcional"
        ></textarea>
      </div>
      <div class="form-group">
        <label>Grupo</label>
        <select v-model="newCard.group_id" class="form-control">
          <option :value="null">Global (todos os grupos)</option>
          <option v-for="group in groups" :key="group.id" :value="group.id">
            {{ group.name }}
          </option>
        </select>
        <small class="text-muted">
          Ficha global serve de fallback. Fichas com grupo têm prioridade para participantes do grupo.
        </small>
      </div>
      <div class="form-group mb-0">
        <label class="d-flex align-items-center">
          <input v-model="newCard.is_default" type="checkbox" class="mr-2" />
          Marcar como ficha padrão
        </label>
        <small class="text-muted">
          A ficha padrão é usada quando nenhuma outra atende o participante.
        </small>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import Swal from 'sweetalert2'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import http from '@/http'
import services from './services'
import { errorMessage, successMessage } from '@/components/alertsMessages'

export default {
  components: {
    Layout,
    PageHeader
  },
  data: function () {
    return {
      title: 'Fichas do Participante',
      items: [
        { text: 'Configurações' },
        { text: 'Fichas do Participante', active: true }
      ],
      cards: [],
      groups: [],
      loading: false,
      search: '',
      showNewCardModal: false,
      newCard: {
        name: '',
        description: '',
        group_id: null,
        is_active: true,
        is_default: false
      }
    }
  },
  mounted: function () {
    this.loadGroups()
    this.loadCards()
  },
  methods: {
    loadCards: function () {
      var self = this
      self.loading = true
      var params = { all: true }
      if (self.search) params.search = self.search
      services.getCards(params)
        .then(function (response) {
          self.cards = response.data || []
        })
        .catch(function (error) {
          var msg = error.response && error.response.data && error.response.data.message
          errorMessage(msg || 'Erro ao carregar fichas')
        })
        .finally(function () {
          self.loading = false
        })
    },
    loadGroups: function () {
      var self = this
      http.get('/admin/groups', { params: { all: true } })
        .then(function (response) {
          self.groups = response.data.data || response.data || []
        })
        .catch(function () {
          // Silencia: se grupos não carregarem, o usuário ainda pode criar fichas globais
        })
    },
    openNewCardModal: function () {
      this.newCard = {
        name: '',
        description: '',
        group_id: null,
        is_active: true,
        is_default: false
      }
      this.showNewCardModal = true
    },
    createCard: function () {
      var self = this
      services.createCard(self.newCard)
        .then(function (response) {
          successMessage('Ficha criada com sucesso')
          self.showNewCardModal = false
          var card = response.data
          // Vai direto pro Builder da ficha recém-criada
          self.$router.push({
            name: 'participant-card-builder',
            params: { id: card.id }
          })
        })
        .catch(function (error) {
          var msg = error.response && error.response.data && error.response.data.message
          errorMessage(msg || 'Erro ao criar ficha')
        })
    },
    editCard: function (id) {
      this.$router.push({
        name: 'participant-card-builder',
        params: { id: id }
      })
    },
    duplicateCard: function (card) {
      var self = this
      services.duplicateCard(card.id)
        .then(function () {
          successMessage('Ficha duplicada com sucesso')
          self.loadCards()
        })
        .catch(function (error) {
          var msg = error.response && error.response.data && error.response.data.message
          errorMessage(msg || 'Erro ao duplicar ficha')
        })
    },
    toggleStatus: function (card) {
      var self = this
      services.toggleStatus(card.id)
        .then(function () {
          successMessage(card.is_active ? 'Ficha inativada' : 'Ficha ativada')
          self.loadCards()
        })
        .catch(function (error) {
          var msg = error.response && error.response.data && error.response.data.message
          errorMessage(msg || 'Erro ao alterar status')
        })
    },
    confirmDeleteCard: function (card) {
      var self = this
      Swal.fire({
        title: 'Deletar ficha?',
        text: 'A ficha "' + card.name + '" será removida permanentemente.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sim, deletar',
        cancelButtonText: 'Cancelar'
      }).then(function (result) {
        if (result.value) {
          services.deleteCard(card.id)
            .then(function () {
              successMessage('Ficha deletada')
              self.loadCards()
            })
            .catch(function (error) {
              var msg = error.response && error.response.data && error.response.data.message
              errorMessage(msg || 'Erro ao deletar ficha')
            })
        }
      })
    }
  }
}
</script>

<style scoped>
.link {
  cursor: pointer;
}
.table-responsive {
  min-height: 300px;
}
</style>
